import "./styles.css";
import "./app/i18n";

import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import { env } from "./app/env";
import { createQueryClient } from "./app/queryClient";
import { createAppRouter } from "./app/router";
import { initObservability } from "./app/observability";
import { flushPendingLogout } from "./shared/auth/session";
import { captureInstallPrompt } from "./shared/offline/install";
import { onSwMessage } from "./shared/offline/swMessages";
import { applyTheme, getTheme } from "./shared/lib/theme";

const queryClient = createQueryClient();
const router = createAppRouter(queryClient);

const root = document.getElementById("root");
if (!root) throw new Error("#root element missing");

applyTheme(getTheme());
// FE-04 FR-FE04-14: keep an early `beforeinstallprompt` for "Instalar app".
captureInstallPrompt();
// FE-04 FR-FE04-37: a tapped notification navigates inside the open app (same-origin path only).
onSwMessage((message) => {
  if (message.type === "navigate" && message.url.startsWith("/") && !message.url.startsWith("//")) {
    void router.navigate({ href: message.url });
  }
});

/**
 * FE-04 FR-FE04-18/20: on the app areas, restore the persisted queries (offline `me` included) and
 * load the outbox before the router's first guard. Public, pay and auth pages skip both, so they
 * never download the persister or the outbox (FE-01 NFR-FE01-02).
 */
async function prepareOfflineData(): Promise<void> {
  if (!/^\/(app|portal)(\/|$)/.test(window.location.pathname)) return;
  if (/^\/(app\/(login|register)|portal\/login)(\/|$)/.test(window.location.pathname)) return;
  const [{ restoreQueries }, { initOutbox }] = await Promise.all([
    import("./shared/offline/persister"),
    import("./shared/offline/outbox"),
  ]);
  await Promise.all([restoreQueries(queryClient), initOutbox()]);
}

// FE-05 FR-FE05-24: a logout that happened offline is sent before `me` is asked.
void Promise.all([flushPendingLogout(), prepareOfflineData().catch(() => undefined)]).finally(
  () => {
    createRoot(root).render(
      <StrictMode>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
        </QueryClientProvider>
      </StrictMode>,
    );
    // FE-01 NFR-FE01-08: Sentry + Web Vitals after the first render, staging/production only.
    const start = () => void initObservability(env).catch(() => undefined);
    if ("requestIdleCallback" in window) window.requestIdleCallback(start);
    else setTimeout(start, 1000);
  },
);
